import type { SlotSymbolDef } from '../../widgets/slot/types';
import type { WheelSegment } from '../../widgets/wheel/types';
import type { Theme } from '../Theme';

const SYMBOLS: readonly SlotSymbolDef[] = [
  { id: 'cherry', glyph: '🌙', tint: 0x9aaaff, weight: 5 },
  { id: 'lemon', glyph: '✨', tint: 0xb38bff, weight: 5 },
  { id: 'orange', glyph: '🌌', tint: 0x6464c4, weight: 4 },
  { id: 'bell', glyph: '🔮', tint: 0xa46bff, weight: 3 },
  { id: 'star', glyph: '⭐', tint: 0xffe28a, weight: 2 }, // wild
  { id: 'seven', glyph: '🪐', tint: 0xffa844, weight: 1 },
  { id: 'gem', glyph: '💠', tint: 0x42e2f5, weight: 1 },
  { id: 'scatter', glyph: '🌠', tint: 0xb38bff, weight: 1 }, // scatter
  // Money symbol — re-skinned as a star-orb (the cosmic-treasure
  // metaphor) so the celestial palette stays coherent. Weight + trigger
  // tuned for demo (see defaultTheme money entry).
  { id: 'money', glyph: '💫', tint: 0xc8a01f, weight: 1.5, isMoney: true },
];

const SEGMENTS: readonly WheelSegment[] = [
  { id: 'x1-a', kind: 'multiplier', label: '×1', tint: 0x9aaaff, multiplier: 1, weight: 4 },
  { id: 'x2-a', kind: 'multiplier', label: '×2', tint: 0x6bd0ff, multiplier: 2, weight: 3 },
  { id: 'x0-a', kind: 'multiplier', label: '✕', tint: 0x232548, multiplier: 0, weight: 2 },
  { id: 'x3-a', kind: 'multiplier', label: '×3', tint: 0xb38bff, multiplier: 3, weight: 2 },
  { id: 'x1-b', kind: 'multiplier', label: '×1', tint: 0x9aaaff, multiplier: 1, weight: 3 },
  { id: 'x5-a', kind: 'multiplier', label: '×5', tint: 0xffa844, multiplier: 5, weight: 1 },
  { id: 'x0-b', kind: 'multiplier', label: '✕', tint: 0x232548, multiplier: 0, weight: 2 },
  { id: 'x10-a', kind: 'multiplier', label: '×10', tint: 0xffe28a, multiplier: 10, weight: 1 },
  { id: 'x2-b', kind: 'multiplier', label: '×2', tint: 0x6bd0ff, multiplier: 2, weight: 3 },
  { id: 'x1-c', kind: 'multiplier', label: '×1', tint: 0x9aaaff, multiplier: 1, weight: 3 },
  { id: 'x3-b', kind: 'multiplier', label: '×3', tint: 0xb38bff, multiplier: 3, weight: 2 },
  { id: 'jackpot', kind: 'multiplier', label: '×25', tint: 0xffd23b, multiplier: 25, weight: 1 },
];

/**
 * Midnight theme — celestial palette (deep blue + violet + warm star
 * accent). Validates the theme system: same game maths, different visuals.
 *
 * <p>Symbol ids match {@link DEFAULT_THEME} so payout maps are reusable
 * across themes — no need to re-tune the math when reskinning. cs-core's
 * MMS works the same way: one math model, many sprite skins.
 */
export const MIDNIGHT_THEME: Theme = {
  id: 'midnight',
  name: 'Midnight',
  palette: {
    accent: 0xb38bff,         // violet
    secondary: 0x6bd0ff,      // sky-blue
    warm: 0xffe28a,           // warm star yellow
    frameFill: 0x0a0820,      // deep night blue
    bgGradientTop: 0x06051a,
    bgGradientBottom: 0x2a2070,
    dimOverlay: 0x020412,
  },
  slot: {
    symbols: SYMBOLS,
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    segments: SEGMENTS,
  },
};
